import { FAILURE, POST, PUT, SUCCESS } from "../utils/const/http-const";
import { getHeaders } from "./utils";

export const MENSAJE_ACCION_KANBAN_MAX_LENGTH = 1024;

export function crearConfigAccionesSolicitudProductoDefault() {
    return {
        aceptar: {
            mensaje: "",
            notificarA: [],
            estadoSolicitud: null,
        },
        rechazar: {
            mensaje: "",
            notificarA: [],
            estadoSolicitud: null,
        },
        idKanban: "solicitud-producto",
        id: null,
    };
}

const parseJsonField = (value) => {
    if (value && typeof value === "object") return value;
    if (typeof value === "string" && value.trim()) {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch (err) {
            return {};
        }
    }
    return {};
};

const parseNotificarA = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim()) {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (err) {
            return [];
        }
    }
    return [];
};

const parseEstadoSolicitudProducto = (value) => {
    if (!value || typeof value !== "object") return null;
    if (value.value == null) return null;
    return {
        value: value.value,
        label: value.label ?? String(value.value),
    };
};

/**
 * Convierte la respuesta MySQL/API al contrato usado por el modal.
 */
export function normalizarConfigAccionesSolicitudProducto(value) {
    const config = value && typeof value === "object" ? value : {};
    const aceptar = parseJsonField(config.aceptar);
    const rechazar = parseJsonField(config.rechazar);

    return {
        aceptar: {
            mensaje: String(aceptar.mensaje || "").slice(
                0,
                MENSAJE_ACCION_KANBAN_MAX_LENGTH
            ),
            notificarA: parseNotificarA(aceptar.notificarA),
            estadoSolicitud: parseEstadoSolicitudProducto(aceptar.estadoSolicitud),
        },
        rechazar: {
            mensaje: String(rechazar.mensaje || "").slice(
                0,
                MENSAJE_ACCION_KANBAN_MAX_LENGTH
            ),
            notificarA: parseNotificarA(rechazar.notificarA),
            estadoSolicitud: parseEstadoSolicitudProducto(rechazar.estadoSolicitud),
        },
        idKanban: config.idKanban ?? "solicitud-producto",
        id: config.id ?? null,
    };
}

/**
 * GET lógico de la configuración OK/X de la columna Solicitud.
 *
 * POST /api/kanban-config/obtener-config-acciones-solicitud
 * body: {}
 *
 * Respuesta esperada:
 * { code: 1, body: { aceptar: {...}, rechazar: {...} } }
 */
export async function obtenerConfigAccionesSolicitudProducto(obj = {}) {
    try {
        const idKanban = obj.idKanban ?? "solicitud-producto";
        const payload = { ...obj, idKanban };
        const { code, body, message } = await fetch(
            `https://gestiondeflota.boltrack.net/apiultimamillav3/api/kanban-config/obtener-lista-kanban-config`,
            {
                headers: getHeaders(),
                method: POST,
                body: JSON.stringify(payload),
            }
        ).then((res) => res.json());

        if (code === SUCCESS && Array.isArray(body)) {
            const match = body.find(
                (r) => String(r?.idKanban) === String(idKanban)
            );
            if (match) {
                return {
                    error: false,
                    data: normalizarConfigAccionesSolicitudProducto(match),
                };
            }
        }
        return {
            error: true,
            message,
            data: crearConfigAccionesSolicitudProductoDefault(),
        };
    } catch (err) {
        console.log("obtenerConfigAccionesSolicitudProducto err");
        console.log(err);
        return {
            error: true,
            message: err,
            data: crearConfigAccionesSolicitudProductoDefault(),
        };
    }
}

/**
 * Guarda en MySQL la configuración completa de ambos botones.
 *
 * PUT /api/kanban-config/guardar-config-acciones-solicitud
 * body:
 * {
 *   aceptar: {
 *     mensaje: string(1024),
 *     notificarA: [{value,label,correo}],
 *     estadoSolicitud: { value, label }
 *   },
 *   rechazar: { mensaje: string(1024), notificarA: [{value,label,correo}] }
 * }
 */
export async function guardarConfigAccionesSolicitudProducto(config) {
    try {
        const payload = normalizarConfigAccionesSolicitudProducto(config);
        const { code, body, message } = await fetch(
            `https://gestiondeflota.boltrack.net/apiultimamillav3/api/kanban-config/crear-kanban-config`,
            {
                headers: getHeaders(),
                method: POST,
                body: JSON.stringify(payload),
            }
        ).then((res) => res.json());

        if (code === SUCCESS) {
            const saved =
                body && typeof body === "object"
                    ? normalizarConfigAccionesSolicitudProducto({ ...payload, ...body })
                    : normalizarConfigAccionesSolicitudProducto({
                        ...payload,
                        id: typeof body === "string" ? body : payload.id,
                    });
            return {
                error: false,
                data: saved,
            };
        }
        if (code === FAILURE) {
            return { error: true, message };
        }
        return { error: true, message };
    } catch (err) {
        console.log("guardarConfigAccionesSolicitudProducto err");
        console.log(err);
        return { error: true, message: err };
    }
}

export async function actualizarConfigAccionesSolicitudProducto(config) {
    try {
        const payload = normalizarConfigAccionesSolicitudProducto(config);
        const { code, body, message } = await fetch(
            `https://gestiondeflota.boltrack.net/apiultimamillav3/api/kanban-config/actualizar-kanban-config`,
            {
                headers: getHeaders(),
                method: PUT,
                body: JSON.stringify(payload),
            }
        ).then((res) => res.json());

        if (code === SUCCESS) {
            const saved =
                body && typeof body === "object"
                    ? normalizarConfigAccionesSolicitudProducto({ ...payload, ...body })
                    : payload;
            return {
                error: false,
                data: saved,
            };
        }
        if (code === FAILURE) {
            return { error: true, message };
        }
        return { error: true, message };
    } catch (err) {
        console.log("actualizarConfigAccionesSolicitudProducto err");
        console.log(err);
        return { error: true, message: err };
    }
}

/**
 * Actualiza el idEstadoSolicitud de una solicitud de producto.
 *
 * PUT /api/solicitud-producto/actualizar-id-estado-solicitud-producto
 * body: { id, idEstadoViaje }
 */
export async function actualizarIdEstadoSolicitudProducto(obj) {
    try {
        let data = { error: true };
        const { code, message, body } = await fetch(
            "https://gestiondeflota.boltrack.net/apiultimamillav3/api/solicitud-producto/actualizar-id-estado-solicitud-producto",
            {
                headers: getHeaders(),
                method: PUT,
                body: JSON.stringify(obj),
            }
        ).then((res) => res.json());

        if (code === SUCCESS) {
            data.error = false;
            data.data = body;
        }
        if (code === FAILURE) {
            data.error = true;
            data.message = message;
        }
        return data;
    } catch (err) {
        console.log("actualizarIdEstadoSolicitudProducto err");
        console.log(err);
        return { error: true, message: err };
    }
}
